import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, XCircle, HelpCircle, MinusCircle, CircleDashed, UserCheck, Sparkles, ExternalLink, Pencil, X, MoreHorizontal, Layers, FileBox, Target, ShieldAlert, DollarSign, ChevronDown } from 'lucide-react';
import { CriterionData, CriterionStatus, CriterionType } from '../lib/types';
const statusConfig: Record<string, {
  icon: React.ElementType;
  label: string;
  badgeCls: string;
  iconCls: string;
}> = {
  satisfied: {
    icon: CheckCircle2,
    label: 'Satisfied',
    badgeCls: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border-0',
    iconCls: 'text-emerald-500'
  },
  'not-satisfied': {
    icon: XCircle,
    label: 'Not Satisfied',
    badgeCls: 'bg-red-50 text-red-700 hover:bg-red-50 border-0',
    iconCls: 'text-red-500'
  },
  unknown: {
    icon: HelpCircle,
    label: 'Unknown',
    badgeCls: 'bg-amber-50 text-amber-700 hover:bg-amber-50 border-0',
    iconCls: 'text-amber-500'
  },
  'not-checked': {
    icon: CircleDashed,
    label: 'Not Checked',
    badgeCls: 'bg-slate-50 text-slate-600 hover:bg-slate-50 border-0',
    iconCls: 'text-slate-400'
  },
  'not-applicable': {
    icon: MinusCircle,
    label: 'N/A',
    badgeCls: 'bg-slate-50 text-slate-500 hover:bg-slate-50 border-0',
    iconCls: 'text-slate-400'
  },
  'manual-review': {
    icon: UserCheck,
    label: 'Requires Manual Review',
    badgeCls: 'bg-amber-50 text-amber-700 hover:bg-amber-50 border-0',
    iconCls: 'text-amber-500'
  }
};
const typeConfig: Record<string, {
  badgeCls: string;
}> = {
  Explicit: {
    badgeCls: 'border-indigo-300 text-indigo-600 bg-indigo-50'
  },
  Implicit: {
    badgeCls: 'border-purple-300 text-purple-600 bg-purple-50'
  },
  Eligibility: {
    badgeCls: 'border-amber-300 text-amber-700 bg-amber-50'
  },
  Scope: {
    badgeCls: 'border-sky-300 text-sky-700 bg-sky-50'
  },
  Disqualifications: {
    badgeCls: 'border-red-300 text-red-700 bg-red-50'
  },
  Budget: {
    badgeCls: 'border-emerald-300 text-emerald-700 bg-emerald-50'
  }
};
const artifactIconMap: Record<string, React.ElementType> = {
  Overall: Layers
};
interface RequirementsTabProps {
  criteria: CriterionData[];
  artifactNameMap?: Record<string, string>;
}
export function RequirementsTab({
  criteria,
  artifactNameMap = {}
}: RequirementsTabProps) {
  const [openMoreId, setOpenMoreId] = useState<string | null>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  // Source viewer state
  const [sourceViewer, setSourceViewer] = useState<{
    url: string;
    title: string;
  } | null>(null);
  // Filter state
  const [filterType, setFilterType] = useState<'all' | string>('all');
  const [filterAppliesTo, setFilterAppliesTo] = useState<'all' | string>('all');
  const [filterCheckStatus, setFilterCheckStatus] = useState<'all' | string>('all');
  const [openFilterDropdown, setOpenFilterDropdown] = useState<string | null>(null);
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const filterBarRef = useRef<HTMLDivElement>(null);
  const [filterBarHeight, setFilterBarHeight] = useState(0);
  // Compute unique types and artifact values from data
  const uniqueTypes = useMemo(() => {
    const types = new Set<string>();
    criteria.forEach(c => types.add(c.type));
    return Array.from(types);
  }, [criteria]);
  const resolveArtifactName = (art: string) => {
    if (art === 'Overall') return 'Overall';
    return artifactNameMap[art] || art;
  };
  const uniqueArtifacts = useMemo(() => {
    const arts = new Set<string>();
    criteria.forEach(c => {
      const artifacts = Array.isArray(c.artifact) ? c.artifact : [c.artifact];
      artifacts.forEach(a => arts.add(a));
    });
    return Array.from(arts).sort((a, b) => {
      if (a === 'Overall') return -1;
      if (b === 'Overall') return 1;
      return resolveArtifactName(a).localeCompare(resolveArtifactName(b));
    });
  }, [criteria, artifactNameMap]);
  const uniqueStatuses = useMemo(() => {
    const statuses = new Set<string>();
    criteria.forEach(c => statuses.add(c.status));
    return Array.from(statuses);
  }, [criteria]);
  // Filter bar height tracking
  useEffect(() => {
    if (!filterBarRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setFilterBarHeight(entry.contentRect.height + parseFloat(getComputedStyle(entry.target).paddingTop) * 2 + 1);
    });
    ro.observe(filterBarRef.current);
    const el = filterBarRef.current;
    setFilterBarHeight(el.offsetHeight);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setOpenMoreId(null);
      }
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target as Node)) {
        setOpenFilterDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Filter logic
  const hasActiveFilters = filterType !== 'all' || filterAppliesTo !== 'all' || filterCheckStatus !== 'all';
  const filteredCriteria = criteria.filter(item => {
    if (filterType !== 'all' && item.type !== filterType) return false;
    if (filterAppliesTo !== 'all') {
      const artifacts = Array.isArray(item.artifact) ? item.artifact : [item.artifact];
      if (!artifacts.includes(filterAppliesTo)) return false;
    }
    if (filterCheckStatus !== 'all' && item.status !== filterCheckStatus) return false;
    return true;
  });
  const clearFilters = () => {
    setFilterType('all');
    setFilterAppliesTo('all');
    setFilterCheckStatus('all');
  };
  return <Card className="border-0 shadow-sm" data-id="element-457">
      <CardContent className="p-0" data-id="element-458">
        <div data-id="element-459">
          {/* Filter Bar */}
          <div ref={filterBarRef} className="px-4 py-2.5 flex items-center gap-2 border-b border-border bg-white sticky top-0 z-20 flex-wrap shadow-[0_1px_0_0_hsl(var(--border))]" data-id="element-460">
            <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mr-1" data-id="element-461">
              Filters
            </span>

            {/* Type Filter */}
            <div className="relative" ref={openFilterDropdown === 'type' ? filterDropdownRef : undefined} data-id="element-462">
              <button onClick={() => setOpenFilterDropdown(openFilterDropdown === 'type' ? null : 'type')} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border transition-colors ${filterType !== 'all' ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100' : 'bg-white text-foreground border-border hover:bg-muted/50'}`} data-id="element-463">
                Type
                {filterType !== 'all' && <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1 rounded" data-id="element-464">
                    {filterType}
                  </span>}
                <ChevronDown className={`h-3 w-3 transition-transform ${openFilterDropdown === 'type' ? 'rotate-180' : ''}`} data-id="element-465" />
              </button>
              {openFilterDropdown === 'type' && <div className="absolute top-full left-0 mt-1 z-50 w-48 rounded-md bg-white border shadow-lg py-1" data-id="element-466">
                  <button onClick={() => {
                setFilterType('all');
                setOpenFilterDropdown(null);
              }} className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs transition-colors ${filterType === 'all' ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-foreground hover:bg-muted'}`} data-id="element-467">
                    All
                  </button>
                  {uniqueTypes.map(type => <button key={type} onClick={() => {
                setFilterType(type);
                setOpenFilterDropdown(null);
              }} className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs transition-colors ${filterType === type ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-foreground hover:bg-muted'}`} data-id="element-468">
                      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-4 ${typeConfig[type]?.badgeCls || 'border-slate-300 text-slate-600 bg-slate-50'}`} data-id="element-469">
                        {type}
                      </Badge>
                    </button>)}
                </div>}
            </div>

            {/* Applies To Filter */}
            <div className="relative" ref={openFilterDropdown === 'appliesTo' ? filterDropdownRef : undefined} data-id="element-470">
              <button onClick={() => setOpenFilterDropdown(openFilterDropdown === 'appliesTo' ? null : 'appliesTo')} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border transition-colors ${filterAppliesTo !== 'all' ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100' : 'bg-white text-foreground border-border hover:bg-muted/50'}`} data-id="element-471">
                Applies To
                {filterAppliesTo !== 'all' && <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1 rounded truncate max-w-[80px]" data-id="element-472">
                    {filterAppliesTo}
                  </span>}
                <ChevronDown className={`h-3 w-3 transition-transform ${openFilterDropdown === 'appliesTo' ? 'rotate-180' : ''}`} data-id="element-473" />
              </button>
              {openFilterDropdown === 'appliesTo' && <div className="absolute top-full left-0 mt-1 z-50 w-56 rounded-md bg-white border shadow-lg py-1 max-h-60 overflow-y-auto" data-id="element-474">
                  <button onClick={() => {
                setFilterAppliesTo('all');
                setOpenFilterDropdown(null);
              }} className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs transition-colors ${filterAppliesTo === 'all' ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-foreground hover:bg-muted'}`} data-id="element-475">
                    All
                  </button>
                  {uniqueArtifacts.map(art => {
                const ArtIcon = artifactIconMap[art] || FileBox;
                return <button key={art} onClick={() => {
                  setFilterAppliesTo(art);
                  setOpenFilterDropdown(null);
                }} className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs transition-colors ${filterAppliesTo === art ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-foreground hover:bg-muted'}`} data-id="element-476">
                        <ArtIcon className="h-3 w-3 flex-shrink-0" data-id="element-477" />
                        <span className="truncate" data-id="element-478">
                          {resolveArtifactName(art)}
                        </span>
                      </button>;
              })}
                </div>}
            </div>

            {/* Check Status Filter */}
            <div className="relative" ref={openFilterDropdown === 'status' ? filterDropdownRef : undefined} data-id="element-479">
              <button onClick={() => setOpenFilterDropdown(openFilterDropdown === 'status' ? null : 'status')} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border transition-colors ${filterCheckStatus !== 'all' ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100' : 'bg-white text-foreground border-border hover:bg-muted/50'}`} data-id="element-480">
                Check Status
                {filterCheckStatus !== 'all' && <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1 rounded" data-id="element-481">
                    {statusConfig[filterCheckStatus]?.label || filterCheckStatus}
                  </span>}
                <ChevronDown className={`h-3 w-3 transition-transform ${openFilterDropdown === 'status' ? 'rotate-180' : ''}`} data-id="element-482" />
              </button>
              {openFilterDropdown === 'status' && <div className="absolute top-full left-0 mt-1 z-50 w-48 rounded-md bg-white border shadow-lg py-1" data-id="element-483">
                  <button onClick={() => {
                setFilterCheckStatus('all');
                setOpenFilterDropdown(null);
              }} className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs transition-colors ${filterCheckStatus === 'all' ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-foreground hover:bg-muted'}`} data-id="element-484">
                    All
                  </button>
                  {uniqueStatuses.map(key => {
                const cfg = statusConfig[key];
                if (!cfg) return null;
                const Icon = cfg.icon;
                return <button key={key} onClick={() => {
                  setFilterCheckStatus(key);
                  setOpenFilterDropdown(null);
                }} className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs transition-colors ${filterCheckStatus === key ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-foreground hover:bg-muted'}`} data-id="element-485">
                        <Icon className={`h-3 w-3 ${cfg.iconCls}`} data-id="element-486" />
                        {cfg.label}
                      </button>;
              })}
                </div>}
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && <button onClick={clearFilters} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors ml-1" data-id="element-487">
                <X className="h-3 w-3" data-id="element-488" />
                Clear
              </button>}
          </div>

          <table className="w-full text-sm table-fixed" data-id="element-489">
            <thead className="sticky z-10" style={{
            top: `${filterBarHeight}px`
          }} data-id="element-490">
              <tr className="border-b bg-white shadow-[0_1px_0_0_hsl(var(--border))]" data-id="element-491">
                <th className="text-left font-medium text-muted-foreground uppercase tracking-wider text-[11px] px-4 py-2 w-[36%]" data-id="element-492">
                  Criterion
                </th>
                <th className="text-left font-medium text-muted-foreground uppercase tracking-wider text-[11px] px-4 py-2 w-[12%]" data-id="element-493">
                  Type
                </th>
                <th className="text-left font-medium text-muted-foreground uppercase tracking-wider text-[11px] px-4 py-2 w-[16%]" data-id="element-494">
                  Applies To
                </th>
                <th className="text-left font-medium text-muted-foreground uppercase tracking-wider text-[11px] px-4 py-2 w-[28%]" data-id="element-495">
                  <div className="flex items-center gap-1.5" data-id="element-496">
                    <span data-id="element-497">Check</span>
                    <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50 border border-violet-200 text-[10px] px-1.5 py-0 h-5 gap-1 shadow-none normal-case tracking-normal" data-id="element-498">
                      <Sparkles className="h-3 w-3" data-id="element-499" />
                      AI Analysis
                    </Badge>
                  </div>
                </th>
                <th className="text-right font-medium text-muted-foreground uppercase tracking-wider text-[11px] px-4 py-2 w-[8%]" data-id="element-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody data-id="element-501">
              {filteredCriteria.map(item => {
              const config = statusConfig[item.status];
              const Icon = config.icon;
              return <tr key={item.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors align-top" data-id="element-502">
                    {/* Consideration + AI source */}
                    <td className="px-4 py-3 overflow-hidden" data-id="element-503">
                      <div className="font-medium text-foreground mb-1 line-clamp-3" data-id="element-504">
                        {item.title}
                      </div>
                      <div className="flex items-center gap-1.5 min-w-0" data-id="element-505">
                        <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50 border border-violet-200 text-[10px] px-1.5 py-0 h-5 gap-1 shadow-none flex-shrink-0 whitespace-nowrap" data-id="element-506">
                          <Sparkles className="h-3 w-3" data-id="element-507" />
                          AI Extract
                        </Badge>
                        <button onClick={() => {
                      if (item.id === 'C-SCOPE-02') {
                        setSourceViewer({
                          url: 'https://cdn.magicpatterns.com/uploads/sJWKMWjqLya6xCpNwNsLD4/Untitled_2.png',
                          title: item.sourceRef
                        });
                      } else if (item.sourceUrl) {
                        window.open(item.sourceUrl, '_blank', 'noopener,noreferrer');
                      }
                    }} className="inline-flex items-center gap-1 text-[10px] text-violet-600 hover:text-violet-800 hover:underline font-medium transition-colors min-w-0 overflow-hidden" data-id="element-508">
                          <ExternalLink className="h-3 w-3 flex-shrink-0" data-id="element-509" />
                          <span className="truncate" data-id="element-510">{item.sourceRef}</span>
                        </button>
                      </div>
                    </td>

                    {/* Type */}
                    <td className="px-4 py-3" data-id="element-511">
                      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-5 ${typeConfig[item.type]?.badgeCls || 'border-slate-300 text-slate-600 bg-slate-50'}`} data-id="element-512">
                        {item.type}
                      </Badge>
                    </td>

                    {/* Artifact(s) */}
                    <td className="px-4 py-3 overflow-hidden" data-id="element-513">
                      <div className="flex flex-col gap-1" data-id="element-514">
                        {(Array.isArray(item.artifact) ? item.artifact : [item.artifact]).map((art, i) => {
                      const ArtifactIcon = artifactIconMap[art] || FileBox;
                      const displayName = resolveArtifactName(art);
                      return <span key={i} className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0 h-5 rounded w-fit ${art === 'Overall' ? 'text-slate-600 bg-slate-100' : 'text-indigo-600 bg-indigo-50'}`} data-id="element-515">
                              <ArtifactIcon className="h-3 w-3 flex-shrink-0" data-id="element-516" />
                              <span className="truncate max-w-[140px]" data-id="element-517">
                                {displayName}
                              </span>
                            </span>;
                    })}
                      </div>
                    </td>

                    {/* AI Check (Status + Notes) */}
                    <td className="px-4 py-3" data-id="element-518">
                      <Badge className={`${config.badgeCls} text-[10px] px-1.5 py-0 h-5 gap-1 mb-1.5`} data-id="element-519">
                        <Icon className="h-3 w-3" data-id="element-520" />
                        {config.label}
                      </Badge>
                      {item.status !== 'manual-review' && item.notes && <p className="text-muted-foreground text-xs leading-relaxed" data-id="element-521">
                          {item.notes}
                        </p>}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3" data-id="element-522">
                      <div className="flex justify-end" data-id="element-523">
                        <div className="relative" ref={openMoreId === item.id ? moreRef : undefined} data-id="element-524">
                          <button onClick={() => setOpenMoreId(openMoreId === item.id ? null : item.id)} className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-border hover:bg-muted transition-colors" data-id="element-525">
                            <MoreHorizontal className="h-3.5 w-3.5 text-muted-foreground" data-id="element-526" />
                          </button>
                          {openMoreId === item.id && <div className="absolute top-full right-0 mt-1 z-50 w-32 rounded-md bg-white border shadow-lg py-1" data-id="element-527">
                              <button onClick={() => setOpenMoreId(null)} className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-foreground hover:bg-muted transition-colors" data-id="element-528">
                                <Pencil className="h-3 w-3" data-id="element-529" />
                                Edit
                              </button>
                              <button onClick={() => setOpenMoreId(null)} className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 transition-colors" data-id="element-530">
                                <X className="h-3 w-3" data-id="element-531" />
                                Dismiss
                              </button>
                            </div>}
                        </div>
                      </div>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </CardContent>

      {/* Source Document Viewer Modal */}
      {sourceViewer && <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setSourceViewer(null)} data-id="element-532">
          <div className="relative bg-white rounded-lg shadow-2xl w-[90vw] max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-533">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-white rounded-t-lg" data-id="element-534">
              <div className="flex items-center gap-2 min-w-0" data-id="element-535">
                <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50 border border-violet-200 text-[10px] px-1.5 py-0 h-5 gap-1 shadow-none flex-shrink-0 whitespace-nowrap" data-id="element-536">
                  <Sparkles className="h-3 w-3" data-id="element-537" />
                  AI Extract Source
                </Badge>
                <span className="text-xs font-medium text-foreground truncate" data-id="element-538">
                  {sourceViewer.title}
                </span>
              </div>
              <button onClick={() => setSourceViewer(null)} className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-muted transition-colors flex-shrink-0" data-id="element-539">
                <X className="h-4 w-4 text-muted-foreground" data-id="element-540" />
              </button>
            </div>
            {/* Image Content */}
            <div className="flex-1 overflow-auto p-4 bg-slate-50" data-id="element-541">
              <img src={sourceViewer.url} alt="Source document" className="w-full rounded-md border border-border shadow-sm" data-id="element-542" />
            </div>
          </div>
        </div>}
    </Card>;
}